import { useState } from 'react';
import { FieldError, useWatch, type Control } from 'react-hook-form';

import { useDateFormatter } from '@mint-lib/formatting-hooks';
import { useTranslation } from '@mint-lib/i18n';
import { InputHelperText, Skeleton } from '@mint-lib/tw-components';
import {
  CalendarRange,
  DatepickerInput,
  DatepickerInputLabel,
  DatepickerInputRoot,
  DateRange,
} from '@mint-lib/tw-components/patterns';
import { parse } from 'date-fns';

import { useMediaPlan } from '../../../../api/queries/useMediaPlan.js';
import { YYYY_MM_DD } from '../../../../consts/index.js';
import { CreateActivityData } from '../../../../utils/createActivityValidation.js';

type ActivityDateRange = {
  control: Control<CreateActivityData>;
  error?: FieldError;
  dateRangeValidationError: string;
  dateRange: DateRange;
  onDateRangeChange: (dateRange: DateRange) => void;
  onApply: (dateRange: DateRange) => void;
};

export const ActivityDateRange = ({
  control,
  error,
  dateRangeValidationError,
  dateRange,
  onDateRangeChange,
  onApply,
}: ActivityDateRange) => {
  const { t } = useTranslation('@myn/planning_v2');

  const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);

  const appliedDateRange = useWatch({
    control,
    name: 'date',
  });

  const { data: mediaPlan } = useMediaPlan();

  const { formattedDateRange } = useDateFormatter(
    appliedDateRange.from,
    appliedDateRange.to,
  );

  const handleSelect = (range?: DateRange) => {
    if (range) {
      onDateRangeChange(range);
    }
  };

  const handleApply = (range?: DateRange) => {
    if (range?.from && range.to) {
      onApply(range);
      setIsDatepickerOpen(false);
    }
  };

  const hasError = Boolean(error || dateRangeValidationError);

  if (!mediaPlan) {
    return <Skeleton className="h-15" />;
  }

  const minDate = parse(mediaPlan.dateFrom, YYYY_MM_DD, new Date());
  const maxDate = parse(mediaPlan.dateTo, YYYY_MM_DD, new Date());

  return (
    <DatepickerInputRoot>
      <DatepickerInputLabel
        label={t('Start and end dates*')}
        htmlFor="activity-date-range-input"
      />
      <DatepickerInput
        className="w-full"
        variant="range"
        color={hasError ? 'error' : 'default'}
        value={formattedDateRange}
        clearable={false}
        open={isDatepickerOpen}
        onOpenChange={() => setIsDatepickerOpen((prev) => !prev)}
        popoverClassName="z-popover"
        isPortal
      >
        <CalendarRange
          selected={dateRange}
          onSelect={handleSelect}
          onApply={handleApply}
          disabledDates={{
            before: minDate,
            after: maxDate,
          }}
          defaultMonth={minDate}
        />
      </DatepickerInput>
      {hasError && (
        <InputHelperText
          color="error"
          helperText={error?.message ?? dateRangeValidationError}
        />
      )}
    </DatepickerInputRoot>
  );
};
